import { Token } from '@stripe/stripe-js'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { compact } from 'lodash'
import moment from 'moment/moment'
import { ComponentProps, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { twMerge as twm } from 'tailwind-merge'

import { useErrorHandler } from '../../hooks/useErrorHandler'
import { BaseRecord } from '../../models/BaseRecord'
import { Channel } from '../../models/Channel'
import { Client } from '../../models/Client'
import { PAYEE_TYPE } from '../../models/Payee'
import { RecordDataMap } from '../../models/RecordDataMap'
import { RecordFactory } from '../../models/RecordFactory'
import { Schedule } from '../../models/Schedule'
import { Source } from '../../models/Source'
import { User } from '../../models/User'
import { apiClient } from '../../modules/apiclient'
import { isValidEmailAddress, isValidPhoneNumber } from '../../utils'
import { Icon } from '../Icon'
import Button from '../app/design/Button'
import Label from '../app/design/Label'
import Modal from '../app/design/Modal'
import Paragraph from '../app/design/Paragraph'
import { AddressPicker } from '../common/AddressPicker'
import { ClientSearchableSelect } from './ClientSearchableSelect'
import { Checkbox } from '../common/fields/Checkbox'
import { Chip } from '../common/fields/Chip'
import { CurrencyField } from '../common/fields/CurrencyField'
import { SearchableSelect } from '../common/fields/SearchableSelect'
import { TextField } from '../common/fields/TextField'
import { CustomFieldRecordModal } from '../items/CustomFieldRecordModal'
import { discountPercentOptions } from '../settings/Options'
import { WorkflowPage } from '../workflows/WorkflowPage'
import { SourceModalWorkflow } from './SourceModalWorkflow'

type Props = {
    defaultClient: Client
    defaultUser: User
    me: User
    owner: User
    onComplete: (user: User) => void
    onClose: ComponentProps<typeof WorkflowPage>['onClose']
    primaryButtonLabel?: string
}

export const NewUserPage = ({
    defaultClient,
    defaultUser,
    me,
    owner,
    onComplete,
    onClose,
    primaryButtonLabel = 'Save',
}: Props) => {
    const { logAndDisplayError } = useErrorHandler()
    const navigate = useNavigate()

    const { data: payees } = useQuery({
        queryKey: ['payees', PAYEE_TYPE.PAYEE_TYPE_STAFF_ONLY],
        queryFn: () => RecordFactory.getInstance().loadPayees(PAYEE_TYPE.PAYEE_TYPE_STAFF_ONLY),
    })
    const { data: leadSources } = useQuery({
        queryKey: ['leadSources'],
        queryFn: () => RecordFactory.getInstance().loadLeadSourceChannels(),
    })
    const { data: fields } = useQuery({
        queryKey: ['fields', owner.id],
        queryFn: () => RecordFactory.getInstance().loadClientFields(owner.id ?? '', false),
    })

    const [client, setClient] = useState(() => {
        if (defaultClient?.referer) {
            return defaultClient.clone({
                channel: new Channel({
                    id: '__client_referral__',
                    name: 'Referred by (Select Client)',
                }),
            })
        }
        else {
            return defaultClient
        }
    })
    const [user, setUser] = useState(defaultUser)
    const [photo, setPhoto] = useState<
        { type: 'file'; file: File } | { type: 'url'; url: string } | null
    >(() => {
        const imageURL = user.image?.url
        if (imageURL === undefined) {
            return null
        }
        return { type: 'url', url: imageURL }
    })

    // If we're editing an existing client then we pull their sources from the DB.
    // If we're creating a new client, then we manage an array of Stripe tokens for
    // new sources in the below newClientSourceTokens state, from which we create
    // local-only Source objects.
    const queryClient = useQueryClient()
    const { data: existingClientSources } = useQuery({
        queryKey: ['sources', user.id],
        queryFn: () => Source.validClientSources(user.id ?? ''),
        enabled: user.id !== undefined,
    })

    const [newClientSourceTokens, setNewClientSourceTokens] = useState<Token[]>([])
    const newClientSources = useMemo(() => {
        if (user.id !== undefined) {
            return undefined
        }

        return compact(
            newClientSourceTokens.map((token) => {
                if (token.card === undefined) {
                    return null
                }
                return new Source({
                    id: token.id,
                    name: token.card.name ?? '',
                    allow_deposit: false,
                    card_scanned: false,
                    allow_payment: false,
                    type: me.sp ? 'debit' : 'credit',
                    token: token.id,
                    card_brand: token.card.brand,
                    card_number: token.card.last4,
                    expired_date: moment(
                        `${token.card.exp_month}/${token.card.exp_year}`,
                        'MM/YYYY'
                    ),
                    postal_code: token.card.address_zip,
                    country_code: token.card.address_country,
                })
            })
        )
    }, [newClientSourceTokens, me.sp])
    const handleNewSourceToken = (token: Token) => {
        setNewClientSourceTokens([...newClientSourceTokens, token])
    }

    const effectiveSources = user.id !== undefined ? existingClientSources : newClientSources

    const handleDeleteSource = async (source: Source) => {
        if (user.id !== undefined) {
            if (source.id === undefined) {
                return
            }
            await RecordFactory.getInstance().deleteSource(source.id)
            queryClient.invalidateQueries({ queryKey: ['sources', user.id] })
        }
        else {
            setNewClientSourceTokens(
                newClientSourceTokens.filter((token) => token.id !== source.id)
            )
        }
    }

    const [isCustomFieldsModalOpen, setIsCustomFieldsModalOpen] = useState(false)
    const [isPaymentMethodsModalOpen, setIsPaymentMethodsModalOpen] = useState(false)

    const [isDeleteConfirmationModalOpen, setIsDeleteConfirmationModalOpen] = useState(false)
    const [loadingDeleteButton, setLoadingDeleteButton] = useState<
        'delete' | 'deleteAndBlock' | null
    >(null)

    const [isLoading, setIsLoading] = useState(false)

    const payeeSearchableItems = [
        {
            id: '__none__',
            title: 'None',
            tertiaryView: <Icon icon="user.small" background="round" className="size-9 shrink-0" />,
            payee: undefined,
        },
        ...(payees ?? []).map((payee) => ({
            id: payee.id ?? '',
            title: payee.name ?? '',
            tertiaryView:
                payee.image?.uri !== undefined ? (
                    <img
                        src={payee.image.uri}
                        className="size-9 rounded-full border-2 border-white"
                    />
                ) : (
                    <Icon icon="user.small" background="round" className="size-9 shrink-0" />
                ),
            payee,
        })),
    ]
    const selectedPayeeItem = useMemo(() => {
        const payee = payees?.find((payee) => payee.id === client.employee?.id)
        if (!payee) {
            return null
        }

        return {
            id: payee?.id ?? '',
            title: payee?.name ?? '',
            tertiaryView:
                payee?.image?.uri !== undefined ? (
                    <img
                        src={payee.image.uri}
                        className="size-6 rounded-full border-2 border-white"
                    />
                ) : (
                    <Icon icon="user.small" background="round" className="size-6 shrink-0" />
                ),
            payee,
        }
    }, [client.employee])
    const handleSelectPayeeWithID = (payeeID: string | null) => {
        const payee = payees?.find((payee) => payee.id === payeeID)
        setClient(client.clone({ employee: payee }))
    }

    const leadSourceOptions = [
        {
            id: '__client_referral__',
            title: `Referred by client (select client)`,
            leadSource: new Channel({
                id: '__client_referral__',
                name: 'Referred by (Select Client)',
            }),
        },
        ...(leadSources ?? []).map((leadSource) => ({
            id: leadSource.id ?? '',
            title: leadSource.name ?? '',
            leadSource,
        })),
    ]

    const handleSelectAddress = async (address: string) => {
        setUser(user.clone({ line1: address }))
        const result = await apiClient.post('/location/parse', { q: address })
        const data = result.data
        if (!data) {
            return
        }
        setUser(
            user.clone({
                line1: data.line1,
                line2: data.line2,
                city: data.city,
                state: data.state,
                postal_code: data.postal_code,
                country_code: data.country_code,
            })
        )
    }

    const hasUnsavedChanges =
        BaseRecord.hasChanges(defaultClient, client) || BaseRecord.hasChanges(defaultUser, user)

    const isReferredByClientOptionSelected = client.channel?.name?.startsWith('Referred by')

    const canSave =
        !!user.name &&
        ((!!user.email && isValidEmailAddress(user.email)) ||
            (!!user.phone && isValidPhoneNumber(user.phone))) &&
        (!isReferredByClientOptionSelected || !!client.referer)

    const onSave = async () => {
        if (!canSave) {
            return
        }

        const newClient =
            client.channel?.id === '__client_referral__'
                ? client.clone({
                      channel: new Channel({ name: 'Referred by (Select Client)' }),
                  })
                : client

        setIsLoading(true)

        const result = await RecordFactory.getInstance().sendClient(
            user,
            newClient,
            photo?.type === 'file' ? photo.file : undefined
        )
        if (!result.ok) {
            setIsLoading(false)
            logAndDisplayError(result.error)
            return
        }

        if (user.id === undefined) {
            await Promise.all(
                (newClientSources ?? []).map((source) => {
                    const newSource = source.clone({ id: undefined, owner: result.value })
                    newSource.token = source.id
                    return RecordFactory.getInstance().sendSource(newSource)
                })
            )
        }

        setIsLoading(false)

        onComplete(result.value)
    }

    const onDelete = async (shouldBlock: boolean) => {
        if (client.id === undefined) {
            return
        }

        setLoadingDeleteButton(shouldBlock ? 'deleteAndBlock' : 'delete')
        const result = await RecordFactory.getInstance().deleteClient(client.id, shouldBlock)
        if (!result.ok) {
            logAndDisplayError(result.error)
            return
        }
        navigate('/clients')
    }

    return (
        <WorkflowPage
            title="Client"
            onBack={() => navigate(-1)}
            onClose={onClose}
            footerContent={
                <div
                    className={twm(
                        'flex w-full items-center gap-x-4',
                        client.id === undefined ? 'justify-end' : 'justify-between'
                    )}
                >
                    {client.id !== undefined && (
                        <Button
                            intent="secondary"
                            className="text-danger-400"
                            onClick={() => setIsDeleteConfirmationModalOpen(true)}
                        >
                            Delete client
                        </Button>
                    )}

                    <Button
                        intent="primary"
                        onClick={onSave}
                        isLoading={isLoading}
                        disabled={!canSave}
                    >
                        {primaryButtonLabel}
                    </Button>
                </div>
            }
        >
            <div className="mt-12 flex w-full flex-col items-center justify-start gap-y-6">
                <WorkflowPage.Card title="General">
                    <WorkflowPage.PhotoPicker
                        onSelectPhoto={(photoFile) => setPhoto({ type: 'file', file: photoFile })}
                        selectedPhoto={photo}
                        onClearPhoto={() => setPhoto(null)}
                    />

                    <WorkflowPage.IconSection title="Name (required)" icon="user.large">
                        <TextField
                            value={user.name ?? ''}
                            onChange={(event) => setUser(user.clone({ name: event.target.value }))}
                            onClear={() => setUser(user.clone({ name: '' }))}
                            placeholder="Enter your name"
                            size="medium"
                        />
                    </WorkflowPage.IconSection>

                    <WorkflowPage.IconSection title="Company" icon="briefcase.large">
                        <TextField
                            value={user.entity ?? ''}
                            onChange={(event) =>
                                setUser(user.clone({ entity: event.target.value }))
                            }
                            onClear={() => setUser(user.clone({ entity: '' }))}
                            placeholder="Enter company name"
                            size="medium"
                        />
                    </WorkflowPage.IconSection>

                    <WorkflowPage.IconSection title="Mobile number" icon="phone.large">
                        <TextField
                            value={user.phone ?? ''}
                            onChange={(event) => setUser(user.clone({ phone: event.target.value }))}
                            onClear={() => setUser(user.clone({ phone: '' }))}
                            placeholder="Enter mobile number"
                            size="medium"
                            mask="(000) 000-0000"
                        />
                    </WorkflowPage.IconSection>

                    <WorkflowPage.IconSection title="Email address" icon="envelope.large">
                        <TextField
                            value={user.email ?? ''}
                            onChange={(event) => setUser(user.clone({ email: event.target.value }))}
                            onClear={() => setUser(user.clone({ email: '' }))}
                            placeholder="Enter email address"
                            size="medium"
                        />
                    </WorkflowPage.IconSection>
                </WorkflowPage.Card>

                <WorkflowPage.Card title="Notifications">
                    <WorkflowPage.IconSection
                        title="Send via"
                        icon="messageArrowUp.large"
                        description="Choose how to send notifications"
                    >
                        <div className="flex flex-wrap items-center justify-start gap-x-2 gap-y-1">
                            <Chip
                                title="Text message"
                                isSelected={client.notify === 'sms'}
                                onClick={() => setClient(client.clone({ notify: 'sms' }))}
                                size="small"
                            />
                            <Chip
                                title="Text and email"
                                isSelected={client.notify === 'combo'}
                                onClick={() => setClient(client.clone({ notify: 'combo' }))}
                                size="small"
                            />
                            <Chip
                                title="Email"
                                isSelected={client.notify === 'email'}
                                onClick={() => setClient(client.clone({ notify: 'email' }))}
                                size="small"
                            />
                        </div>
                    </WorkflowPage.IconSection>

                    <WorkflowPage.IconSection
                        title="Payment reminder"
                        icon="moneyAlarmClock.large"
                        description="Choose when to send reminders"
                    >
                        <div className="flex flex-wrap items-center justify-start gap-x-2 gap-y-2">
                            <Chip
                                title="2 hours"
                                isSelected={client.reminder === '2'}
                                onClick={() => setClient(client.clone({ reminder: '2' }))}
                                size="small"
                            />
                            <Chip
                                title="4 hours"
                                isSelected={client.reminder === '4'}
                                onClick={() => setClient(client.clone({ reminder: '4' }))}
                                size="small"
                            />
                            <Chip
                                title="6 hours"
                                isSelected={client.reminder === '6'}
                                onClick={() => setClient(client.clone({ reminder: '6' }))}
                                size="small"
                            />
                            <Chip
                                title="8 hours"
                                isSelected={client.reminder === '8'}
                                onClick={() => setClient(client.clone({ reminder: '8' }))}
                                size="small"
                            />
                            <Chip
                                title="12 hours"
                                isSelected={client.reminder === '12'}
                                onClick={() => setClient(client.clone({ reminder: '12' }))}
                                size="small"
                            />
                            <Chip
                                title="24 hours"
                                isSelected={client.reminder === '24'}
                                onClick={() => setClient(client.clone({ reminder: '24' }))}
                                size="small"
                            />
                            <Chip
                                title="36 hours"
                                isSelected={client.reminder === '36'}
                                onClick={() => setClient(client.clone({ reminder: '36' }))}
                                size="small"
                            />
                            <Chip
                                title="2 days"
                                isSelected={client.reminder === '48'}
                                onClick={() => setClient(client.clone({ reminder: '48' }))}
                                size="small"
                            />
                            <Chip
                                title="3 days"
                                isSelected={client.reminder === '72'}
                                onClick={() => setClient(client.clone({ reminder: '72' }))}
                                size="small"
                            />
                        </div>
                    </WorkflowPage.IconSection>

                    <WorkflowPage.IconSection
                        title="Send smart campaigns"
                        icon="flag.large"
                        description="Send custom notifications"
                        hasHoverEffect
                        secondaryView={
                            <Checkbox
                                isChecked={client.allowTrigger ?? true}
                                onChange={(isChecked) =>
                                    setClient(client.clone({ allow_trigger: isChecked }))
                                }
                            />
                        }
                    />
                </WorkflowPage.Card>

                <WorkflowPage.Card title="Address">
                    <WorkflowPage.IconSection title="Street" icon="globeClock.large">
                        <AddressPicker
                            placeholder="Enter street"
                            onSelect={handleSelectAddress}
                            searchValue={user.line1 ?? ''}
                            setSearchValue={(value) => setUser(user.clone({ line1: value }))}
                            size="medium"
                        />
                    </WorkflowPage.IconSection>

                    <WorkflowPage.IconSection title="Unit" icon="hash.large">
                        <TextField
                            value={user.line2 ?? ''}
                            onChange={(event) => setUser(user.clone({ line2: event.target.value }))}
                            onClear={() => setUser(user.clone({ line2: '' }))}
                            placeholder="Enter unit"
                            size="medium"
                        />
                    </WorkflowPage.IconSection>

                    <WorkflowPage.IconSection title="City" icon="markerPin.large">
                        <TextField
                            value={user.city ?? ''}
                            onChange={(event) => setUser(user.clone({ city: event.target.value }))}
                            onClear={() => setUser(user.clone({ city: '' }))}
                            placeholder="Enter city"
                            size="medium"
                        />
                    </WorkflowPage.IconSection>

                    <WorkflowPage.IconSection title="Zipcode" icon="globeClock.large">
                        <TextField
                            value={user.postalCode ?? ''}
                            onChange={(event) =>
                                setUser(user.clone({ postal_code: event.target.value }))
                            }
                            onClear={() => setUser(user.clone({ postal_code: '' }))}
                            placeholder="Enter zipcode"
                            size="medium"
                        />
                    </WorkflowPage.IconSection>
                </WorkflowPage.Card>

                <WorkflowPage.Card title="More details">
                    <WorkflowPage.IconSection
                        title="Pro"
                        icon="employeeCard.large"
                        description="Staff somebody with this client"
                    >
                        <SearchableSelect
                            items={payeeSearchableItems}
                            onSelectItem={(item) =>
                                handleSelectPayeeWithID(item?.payee?.id ?? null)
                            }
                            selectedItemOverride={selectedPayeeItem}
                            placeholder="Type to search"
                            size="medium"
                            shouldPreserveLastSelection
                        />
                    </WorkflowPage.IconSection>

                    <WorkflowPage.IconSection
                        title="More details"
                        icon="form2.large"
                        description="Add more details"
                        hasHoverEffect
                        secondaryView={
                            <Button
                                onClick={() => setIsCustomFieldsModalOpen(true)}
                                intent="tertiaryLink"
                                className="hover:text-primary-700/60 mr-2 translate-x-3"
                                size="small"
                            >
                                Add
                            </Button>
                        }
                    />

                    <WorkflowPage.IconSection
                        title="Payment methods"
                        icon="cards.large"
                        description={
                            effectiveSources === undefined
                                ? 'Loading...'
                                : effectiveSources.length === 0
                                  ? 'No payment methods'
                                  : effectiveSources.length === 1
                                    ? '1 payment method'
                                    : `${effectiveSources.length} payment methods`
                        }
                        descriptionClassName="text-secondary-400"
                        hasHoverEffect
                        secondaryView={
                            <Button
                                onClick={() => setIsPaymentMethodsModalOpen(true)}
                                intent="tertiaryLink"
                                className="hover:text-primary-700/60 mr-2 translate-x-3"
                                size="small"
                            >
                                Edit
                            </Button>
                        }
                    />

                    <WorkflowPage.IconSection title="Sent lead source" icon="megaphone.large">
                        <div className="flex flex-col items-stretch justify-start gap-y-8">
                            <SearchableSelect
                                size="medium"
                                items={leadSourceOptions}
                                selectedItemID={client.channel?.id}
                                onSelectItem={(option) =>
                                    setClient(client.clone({ channel: option?.leadSource }))
                                }
                                placeholder="None"
                            />

                            {client.channel !== undefined &&
                                (client.channel?.name !== undefined &&
                                isReferredByClientOptionSelected ? (
                                    <div>
                                        <Label className="mb-3 block">Client</Label>
                                        <ClientSearchableSelect
                                            mode="server"
                                            selectedClient={client.referer}
                                            onSelectClient={(referer) =>
                                                setClient(client.clone({ referer, cost: 0 }))
                                            }
                                            shouldPreserveLastSelection
                                        />
                                    </div>
                                ) : (
                                    <div>
                                        <Label className="mb-1 block">Cost</Label>
                                        <Paragraph
                                            size="sm"
                                            className="mb-3 block font-light text-gray-600"
                                        >
                                            Enter the amount paid for this lead
                                        </Paragraph>
                                        <CurrencyField
                                            value={client.cost ?? 0}
                                            onChange={(cost) => setClient(client.clone({ cost }))}
                                            size="medium"
                                        />
                                    </div>
                                ))}
                        </div>
                    </WorkflowPage.IconSection>

                    <WorkflowPage.IconSection title="Discount" icon="discount.large">
                        <SearchableSelect
                            size="medium"
                            items={discountPercentOptions.map((option) => ({
                                id: option.value,
                                title: option.label,
                            }))}
                            selectedItemID={(client.discount ?? '').toString()}
                            onSelectItem={(option) =>
                                setClient(
                                    client.clone({
                                        discount: option?.id ?? '',
                                    })
                                )
                            }
                            shouldPreserveLastSelection
                        />
                    </WorkflowPage.IconSection>

                    <WorkflowPage.IconSection title="Timezone" icon="globeClock.large">
                        <div className="flex flex-wrap items-center justify-start gap-x-2 gap-y-1">
                            {Schedule.timezoneStrings().map((option) => (
                                <Chip
                                    key={option.id}
                                    title={option.label ?? ''}
                                    isSelected={user.timezone === option.id}
                                    onClick={() => setUser(user.clone({ timezone: option.id }))}
                                    size="small"
                                />
                            ))}
                        </div>
                    </WorkflowPage.IconSection>
                </WorkflowPage.Card>
            </div>

            <CustomFieldRecordModal
                isOpen={isCustomFieldsModalOpen}
                onClose={() => setIsCustomFieldsModalOpen(false)}
                title="More details"
                fields={fields ?? []}
                answers={client.fieldValues ?? {}}
                onUpdateAnswers={(newFieldValues) =>
                    setClient(client.clone({ field_values: new RecordDataMap(newFieldValues) }))
                }
                shouldEnforceRequired={false}
            />

            <SourceModalWorkflow
                isOpen={isPaymentMethodsModalOpen}
                onClose={() => setIsPaymentMethodsModalOpen(false)}
                sources={effectiveSources ?? []}
                onCreateSource={
                    user.id !== undefined
                        ? {
                              action: 'create-source',
                              client: user,
                              callback: () => {
                                  queryClient.invalidateQueries({
                                      queryKey: ['sources', user.id],
                                  })
                              },
                          }
                        : {
                              action: 'return-source-token',
                              callback: handleNewSourceToken,
                          }
                }
                onDeleteSource={handleDeleteSource}
            />

            <Modal
                isOpen={isDeleteConfirmationModalOpen}
                onClose={() => setIsDeleteConfirmationModalOpen(false)}
                size="small"
                title="Delete client?"
                showDividers={false}
                footer={
                    <div className="flex w-full items-center justify-end gap-x-4">
                        <Button
                            intent="secondary"
                            className="text-danger-400"
                            isLoading={loadingDeleteButton === 'deleteAndBlock'}
                            onClick={() => onDelete(true)}
                        >
                            Yes, delete & block
                        </Button>
                        <Button
                            intent="destructive"
                            isLoading={loadingDeleteButton === 'delete'}
                            onClick={() => onDelete(false)}
                        >
                            Yes, delete
                        </Button>
                    </div>
                }
            >
                <Paragraph className="text-gray-600" size="md">
                    Deleting a client will remove them from your client list. Deleting & blocking a
                    client will remove them from your client list and they won’t be able to contact
                    or book you in the future
                </Paragraph>
            </Modal>

            <WorkflowPage.NavigationBlocker
                when={hasUnsavedChanges}
                allowNextPaths={[/\/client/]}
            />
        </WorkflowPage>
    )
}
